import { chainSettings, defaultNativeAddress, type ChainType } from "@/constants/chain-specific";
import type { TokenConfigType } from "@/constants/types/currency";
import { delay } from "@/util/delay";

const MAX_ADDRESSES_PER_CALL = 30;
const MAX_CALLS_PER_MINUTE = 5;
const DELAY_BETWEEN_BATCHES = (60 / MAX_CALLS_PER_MINUTE) * 1000; // in milliseconds

export async function getTokenPrices(chain: ChainType, tokens: TokenConfigType[]) {
  const prices: Record<string, number> = {}; // Use string as the key type

  const base = chainSettings[chain as ChainType].tokenPriceEndpoints?.geckoterminal;

  const fetchPrices = async (addresses: string[]) => {
    const filteredAddresses = addresses.map(addr =>
      addr.toLowerCase() === defaultNativeAddress.toLowerCase()
        ? chainSettings[chain as ChainType].wNative.address
        : addr
    );

    const contractList = filteredAddresses.join(",");
    const endpoint = `${base}${contractList}`;
    try {
      const tokenPrices = await fetch(endpoint);
      if (tokenPrices.ok) {
        const tokenData = await tokenPrices.json();

        const tokenPricesData = tokenData.data?.attributes?.token_prices || {};
        for (const address of addresses) {
          prices[address.toLowerCase()] = Number(tokenPricesData[address.toLowerCase()] || 0);
        }
      } else {
        throw new Error(`Failed to fetch token prices: ${tokenPrices.statusText}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const addressBatches = [];
  for (let i = 0; i < tokens.length; i += MAX_ADDRESSES_PER_CALL) {
    addressBatches.push(tokens.slice(i, i + MAX_ADDRESSES_PER_CALL).map((token) => token.address));
  }

  for (let i = 0; i < addressBatches.length; i++) {
    if (i > 0 && i % MAX_CALLS_PER_MINUTE === 0) {
      await delay(DELAY_BETWEEN_BATCHES);
    }
    await fetchPrices(addressBatches[i]);
  }

  return prices;
}